import { notify, checkedFetch } from './feedback';
import WalletLedgerNotice from './WalletLedgerNotice';
import React, { useState, useEffect } from "react";

const API_BASE = "https://internal-services.viewmax.com.ng";

export default function WalletExpense() {
  const [activeTab, setActiveTab] = useState("local");

  return (
    <div className="expense-page">
      <h1>Wallet Expense</h1>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
        Saved wallet debits include manual records and imported Flutterwave debits. These are separate from business expenses and do not initiate payments.
      </p>

      {/* Tab Switcher */}
      <div className="responsive-row" style={{
        display: 'flex', gap: '0', marginBottom: '25px', borderBottom: '2px solid #eee'
      }}>
        <button
          onClick={() => setActiveTab("local")}
          style={{
            padding: '12px 24px', fontWeight: '600', fontSize: '14px',
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: activeTab === 'local' ? '3px solid #28a745' : '3px solid transparent',
            color: activeTab === 'local' ? '#28a745' : '#888'
          }}
        >
          📋 Saved Wallet Debits
        </button>
        <button
          onClick={() => setActiveTab("ledger")}
          style={{
            padding: '12px 24px', fontWeight: '600', fontSize: '14px',
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: activeTab === 'ledger' ? '3px solid #0070f3' : '3px solid transparent',
            color: activeTab === 'ledger' ? '#0070f3' : '#888'
          }}
        >
          🏦 Live Flutterwave Debits
        </button>
      </div>

      {activeTab === "local" && <LocalExpenseTab />}
      {activeTab === "ledger" && <FlutterwaveLedgerTab />}
    </div>
  );
}

// ==================== TAB 1: Local Expense ====================
function LocalExpenseTab() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: '', source: '', category: '', description: '', logged_by: 'System' });
  const [submitting, setSubmitting] = useState(false);

  const fetchExpenses = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/v1/wallet-expenses`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to load wallet debits');
      setExpenses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load expenses");
    }
    setLoading(false);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const resp = await checkedFetch(`${API_BASE}/v1/wallet-expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expense_category: form.category,
          item_name: form.source,
          amount: parseFloat(form.amount),
          description: form.description,
          created_by: form.logged_by,
          currency: "NGN",
        })
      });
      if (!resp.ok) { const data = await resp.json(); throw new Error(data.error || 'Failed to save wallet debit'); }
      notify('Wallet expense saved.', 'success');
      setShowForm(false);
      setForm({ amount: '', source: '', category: '', description: '', logged_by: 'System' });
      fetchExpenses();
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  useEffect(() => {
    fetchExpenses();
    const refresh = () => fetchExpenses();
    window.addEventListener('wallet-synced', refresh);
    return () => window.removeEventListener('wallet-synced', refresh);
  }, []);

  // Separate local vs synced
  const localEntries = expenses.filter(i => i.origin === 'manual');
  const syncedEntries = expenses.filter(i => i.origin === 'flutterwave');
  const totalExpense = expenses.reduce((sum, i) => sum + Number(i.amount), 0);

  return (
    <div>
      {/* Summary */}
      <div className="responsive-row" style={{
        display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap'
      }}>
        <div style={{ padding: '15px 25px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Expense (All)</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#16a34a' }}>₦{totalExpense.toLocaleString()}</div>
        </div>
        <div style={{ padding: '15px 25px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Manual Entries</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#0284c7' }}>{localEntries.length}</div>
        </div>
        <div style={{ padding: '15px 25px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Synced from Flutterwave</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#ca8a04' }}>{syncedEntries.length}</div>
        </div>
      </div>

      {/* Add Expense Button & Form */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '10px 20px', background: showForm ? '#dc3545' : '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
          }}
        >
          {showForm ? '✕ Cancel' : '+ Record Expense'}
        </button>
      </div>

      {showForm && (
        <form className="expense-entry-form" onSubmit={handleAddExpense} style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '15px',
          background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee', marginBottom: '20px'
        }}>
          <input type="number" placeholder="Amount" value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <input type="text" placeholder="Payee or purpose" value={form.source}
            onChange={e => setForm({ ...form, source: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <input type="text" placeholder="Category" value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <input type="text" placeholder="Description" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <button type="submit" disabled={submitting} style={{
            gridColumn: '1 / -1', padding: '10px', background: '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
          }}>
            {submitting ? '⏳ Saving...' : '💾 Save Expense'}
          </button>
        </form>
      )}

      {error && <p className="error-message">{error}</p>}

      {loading ? <p>Loading expense records...</p> : (
        <div className="table-container">
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Payee / Purpose</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Origin</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length > 0 ? expenses.map((inc) => {
                const isSynced = inc.origin === 'flutterwave';
                return (
                  <tr key={inc.id} style={{ backgroundColor: isSynced ? '#fefce8' : '#fafffa' }}>
                    <td>{new Date(inc.created_at || inc.date).toLocaleString()}</td>
                    <td style={{ fontSize: '12px', maxWidth: '250px', wordWrap: 'break-word' }}>{inc.item_name}</td>
                    <td>{inc.expense_category}</td>
                    <td style={{ color: '#dc3545', fontWeight: 'bold' }}>-₦{Number(inc.amount).toLocaleString()}</td>
                    <td>
                      <span style={{
                        background: isSynced ? '#fde68a' : '#d4edda',
                        color: isSynced ? '#92400e' : '#155724',
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
                      }}>
                        {isSynced ? '🔄 Flutterwave' : '✍️ Manual'}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', color: '#555' }}>{inc.description}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No expense records found. Click "+ Record Expense" or sync from Live Flutterwave Debits tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================== TAB 2: Flutterwave Ledger ====================
function FlutterwaveLedgerTab() {
  const [statement, setStatement] = useState([]);
  const [savedFallback, setSavedFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const fetchExpenses = async (pageNum = 1, append = false) => {
    setLoading(true);
    setError("");
    try {
      const today = new Date().toISOString().split('T')[0];
      const finalEndDate = endDate > today ? today : endDate;
      const url = `${API_BASE}/flw/statement?currency=NGN&from=${startDate}&to=${finalEndDate}&size=50&page=${pageNum}&type=D${append && savedFallback ? '&source=saved' : ''}`;
      const resp = await fetch(url, { cache: 'no-store' });
      const data = await resp.json();

      if (data.status === "success" && data.data) {
        setSavedFallback(data.source === 'saved');
        const credits = data.data.transactions || [];
        if (append && !data.reset) {
          setStatement(prev => [...prev, ...credits]);
        } else {
          setStatement(credits);
        }
        if (data.data.page_info) {
          setHasMore(data.data.page_info.current_page < data.data.page_info.total_pages);
          setPage(data.data.page_info.current_page);
        }
      } else {
        throw new Error(data.message || "Failed to fetch expenses");
      }
    } catch (err) {
      if (append) {
        notify(err.message || 'Could not load more entries. Please retry.', 'error');
      } else {
      setError(err.message || "Unexpected error")
      };
    }
    setLoading(false);
  };

  const loadMore = () => { fetchExpenses(page + 1, true); };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const resp = await fetch(`${API_BASE}/flw/sync-expense`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: startDate, to: endDate })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || data.message || 'Wallet sync failed');
      setSyncResult(data);
      if (data.success) window.dispatchEvent(new Event('wallet-synced'));
    } catch (err) {
      setSyncResult({ success: false, error: err.message });
    }
    setSyncing(false);
  };

  useEffect(() => { fetchExpenses(); }, []);

  useEffect(() => {
    const refresh = () => fetchExpenses(1, false);
    window.addEventListener('wallet-synced', refresh);
    return () => window.removeEventListener('wallet-synced', refresh);
  }, [startDate, endDate]);

  return (
    <div>
      <p style={{ color: '#555', fontSize: '13px', marginBottom: '15px' }}>
        Live outgoing debits from Flutterwave. Use <strong>Save to App Database</strong> to keep a copy in Saved Wallet Debits. Saving does not initiate a payment or add a business expense to Reports.
      </p>

      {/* Date Filters + Sync Button */}
      <div className="responsive-row" style={{
        display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '15px',
        padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee',
        flexWrap: 'wrap'
      }}>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <button onClick={() => fetchExpenses(1, false)} disabled={loading}
          style={{
            padding: '10px 20px', background: loading ? '#ccc' : '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600'
          }}
        >
          {loading ? '⏳ Updating...' : '🔍 Filter Expense'}
        </button>
        <button onClick={handleSync} disabled={syncing}
          style={{
            padding: '10px 20px', background: syncing ? '#ccc' : '#0070f3', color: 'white',
            border: 'none', borderRadius: '4px', cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: '600'
          }}
        >
          {syncing ? '⏳ Syncing...' : '🔄 Save to App Database'}
        </button>
      </div>

      {/* Sync Result Message */}
      {syncResult && (
        <div style={{
          padding: '12px 15px', marginBottom: '15px', borderRadius: '6px',
          background: syncResult.success ? '#d4edda' : '#f8d7da',
          color: syncResult.success ? '#155724' : '#721c24',
          border: `1px solid ${syncResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          fontSize: '13px'
        }}>
          {syncResult.success
            ? `✅ Sync complete! Fetched ${syncResult.total_fetched} entries — ${syncResult.synced} new synced, ${syncResult.skipped} already existed.`
            : `❌ Sync failed: ${syncResult.error}`
          }
        </div>
      )}

      <WalletLedgerNotice visible={savedFallback} />
      {loading && statement.length === 0 ? <p>Loading expense entries...</p> : error ? <p className="error-message">{error}</p> : (
        <div className="table-container">
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Reference</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Balance After</th>
                <th>Description / Remarks</th>
              </tr>
            </thead>
            <tbody>
              {statement.length > 0 ? (
                <>
                  {statement.map((entry, idx) => (
                    <tr key={idx} style={{ backgroundColor: '#fafffa' }}>
                      <td>{new Date(entry.date).toLocaleString()}</td>
                      <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>{entry.reference}</td>
                      <td><span style={{ background: '#f8d7da', color: '#721c24', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>DEBIT</span></td>
                      <td style={{ color: '#dc3545', fontWeight: 'bold' }}>-₦{Number(entry.amount).toLocaleString()}</td>
                      <td style={{ fontWeight: '500' }}>{entry.balance_after == null ? '—' : `₦${Number(entry.balance_after).toLocaleString()}`}</td>
                      <td style={{ fontSize: '12px', color: '#555' }}>{entry.remarks}</td>
                    </tr>
                  ))}
                  {hasMore && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        <button onClick={loadMore} disabled={loading}
                          style={{ padding: '10px 30px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          {loading ? 'Loading...' : '⬇️ Load More Entries'}
                        </button>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No expense entries found for this date range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
